/** @jsx jsx */
import { jsx } from '@emotion/core';
import styled from '@emotion/styled';
import { CircularProgress } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ICustomTheme } from 'interfaces';
import { SubscribeContext } from 'interfaces/subscribe.types';

/**
 * Délais (ms) avant d'afficher les messages intermédiaires de réassurance.
 * Choisis pour laisser le temps de lire chaque étape (~3s par message).
 */
const STEP_PREPARING_DELAY_MS = 2000;
const STEP_ALMOST_DONE_DELAY_MS = 5000;

const Backdrop = styled.div<{ theme: ICustomTheme }>`
  position: fixed;
  inset: 0;
  z-index: ${({ theme }) => (theme.zIndex?.modal ?? 1300) + 10};
  background-color: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => `${theme.spacing(3)}px`};
`;

const Card = styled.div<{ theme: ICustomTheme }>`
  background-color: ${({ theme }) => theme.palette.background.paper};
  border-radius: 12px;
  padding: ${({ theme }) => `${theme.spacing(4)}px ${theme.spacing(5)}px`};
  max-width: 420px;
  width: 100%;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(2)}px`};
`;

const Title = styled.h2<{ theme: ICustomTheme }>`
  margin: 0;
  font-size: 1.6rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
`;

const Step = styled.p<{ theme: ICustomTheme }>`
  margin: 0;
  font-size: 1.3rem;
  color: ${({ theme }) => theme.palette.text.secondary};
  min-height: 1.5em;
  transition: opacity 0.25s ease-in-out;
`;

const Hint = styled.p<{ theme: ICustomTheme }>`
  margin: ${({ theme }) => `${theme.spacing(1)}px 0 0 0`};
  font-size: 1.2rem;
  font-style: italic;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const StyledSpinner = styled(CircularProgress)<{ theme: ICustomTheme }>(
  ({ theme }) => ({
    color: theme.colors.paletteFromBO.main,
  }),
);

interface SubmittingOverlayProps {
  context: SubscribeContext;
  /**
   * Quand true, affiche l'état de succès (pas de spinner, pas de messages
   * intermédiaires). Utilisé entre la fin de la requête et la redirection.
   */
  isSuccess?: boolean;
  /**
   * Message personnalisé venant du BO (firstLoginConfiguration.validateText).
   * Quand fourni et qu'on est en succès, il remplace le titre par défaut.
   */
  customMessage?: string;
}

/**
 * Overlay plein écran affiché pendant que la requête de submit est en cours.
 * Bloque les interactions et affiche des messages de réassurance progressifs,
 * puis un message de succès avant la redirection.
 */
const SubmittingOverlay = ({
  context,
  isSuccess = false,
  customMessage,
}: SubmittingOverlayProps): JSX.Element => {
  const { t } = useTranslation();
  const [step, setStep] = useState<0 | 1 | 2>(0);

  useEffect(() => {
    if (isSuccess) {
      return undefined;
    }
    const t1 = window.setTimeout(() => setStep(1), STEP_PREPARING_DELAY_MS);
    const t2 = window.setTimeout(() => setStep(2), STEP_ALMOST_DONE_DELAY_MS);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [isSuccess]);

  const defaultTitleKey =
    context === 'autonomous-registration'
      ? 'auth.subscribe.submitting.title_register'
      : 'auth.subscribe.submitting.title_continue';

  const stepKey =
    step === 0
      ? null
      : step === 1
      ? 'auth.subscribe.submitting.step_preparing'
      : 'auth.subscribe.submitting.step_almost_done';

  // En succès : on privilégie le message custom du BO s'il existe,
  // sinon on affiche le message générique de succès traduit.
  const successMessage = customMessage || t('auth.subscribe.success');

  return (
    <Backdrop
      role="dialog"
      aria-modal="true"
      aria-live="polite"
      aria-busy={!isSuccess}
    >
      <Card>
        {!isSuccess && <StyledSpinner size={48} />}
        <Title>{isSuccess ? successMessage : t(defaultTitleKey)}</Title>
        {!isSuccess && <Step>{stepKey ? t(stepKey) : '\u00A0'}</Step>}
      </Card>
    </Backdrop>
  );
};

export default SubmittingOverlay;
